import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/Pagination';
import ToastBanner from '../components/ToastBanner';
import RemoveXmitModal from '../components/users/RemoveXmitModal';
import {
  getXmitDirectoryApi,
  provisionXmitApi,
  assignDirectoryXmitApi,
  removeDirectoryXmitApi,
} from '../api/xmit.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';

function InServiceBadge({ inService }) {
  if (inService === true) {
    return <span style={{ fontSize: 11, fontWeight: 600, color: '#15803D', background: '#DCFCE7', padding: '3px 9px', borderRadius: 99 }}>In service</span>;
  }
  if (inService === false) {
    return <span style={{ fontSize: 11, fontWeight: 600, color: '#B45309', background: '#FEF3C7', padding: '3px 9px', borderRadius: 99 }}>Out of service</span>;
  }
  return <span style={{ fontSize: 11, color: '#94A3B8' }}>Unknown</span>;
}

function DirectoryAssignModal({ row, open, loading, error, onCancel, onConfirm }) {
  const [userId, setUserId] = useState('');

  useEffect(() => {
    if (open) setUserId('');
  }, [open, row?.xmitNumber]);

  if (!open || !row) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center z-[9999] p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 border border-slate-100 shadow-xl flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-bold text-slate-900 text-lg m-0">Assign XMIT to user</h2>
        <p className="text-sm text-slate-600 m-0">
          Link <span className="font-mono font-semibold">{row.xmitNumber}</span> to an MGL user.
        </p>
        <div>
          <label htmlFor="directory-assign-user-id" className="block text-xs font-semibold text-slate-600 mb-2">
            User ID
          </label>
          <input
            id="directory-assign-user-id"
            type="text"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="Paste user UUID from profile URL"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sky-200"
          />
        </div>
        {error && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{error}</div>
        )}
        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onCancel} disabled={loading} className="px-4 py-2 border rounded-lg text-sm font-semibold text-slate-600">Cancel</button>
          <button
            type="button"
            disabled={loading || !userId.trim()}
            onClick={() => onConfirm(userId.trim())}
            className="px-4 py-2 bg-sky-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
          >
            {loading ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function XmitDirectory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [assigned, setAssigned] = useState('');
  const [stagesSource, setStagesSource] = useState('');
  const [stagesError, setStagesError] = useState('');
  const [directoryHint, setDirectoryHint] = useState('');
  const [toast, setToast] = useState(null);
  const [provisionLoading, setProvisionLoading] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  const showToast = (msg, type = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), type === 'error' ? 5000 : 3200);
  };

  const fetchDirectory = useCallback(async () => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (search.trim()) params.search = search.trim();
    if (assigned) params.assigned = assigned;

    const result = await apiHandler(() => getXmitDirectoryApi(params));
    if (result.success) {
      const payload = unwrapData(result) || {};
      setRows(payload.rows || []);
      setTotal(payload.total || 0);
      setTotalPages(payload.totalPages || 1);
      setStagesSource(payload.stagesSource || '');
      setStagesError(payload.stagesError || '');
      setDirectoryHint(payload.hint || '');
    } else {
      setRows([]);
      showToast(result.message || 'Failed to load XMIT directory', 'error');
    }
    setLoading(false);
  }, [page, search, assigned]);

  useEffect(() => {
    fetchDirectory();
  }, [fetchDirectory]);

  async function handleProvision() {
    setProvisionLoading(true);
    const result = await apiHandler(() => provisionXmitApi());
    setProvisionLoading(false);
    if (result.success) {
      const payload = unwrapData(result);
      showToast(`Provisioned XMIT ${payload?.xmitNumber || ''}`, 'success');
      fetchDirectory();
      return;
    }
    showToast(result.message || 'Failed to provision XMIT', 'error');
  }

  async function handleDirectoryAssign(userId) {
    if (!assignTarget) return;
    setAssignLoading(true);
    setAssignError('');
    const result = await apiHandler(() =>
      assignDirectoryXmitApi(assignTarget.xmitNumber, { userId, mode: 'manual' })
    );
    setAssignLoading(false);
    if (result.success) {
      setAssignTarget(null);
      showToast('XMIT assigned successfully', 'success');
      fetchDirectory();
      return;
    }
    if (result.code === 'XMIT_ALREADY_ON_USER') {
      setAssignError('That user already has an XMIT. Remove it first.');
      return;
    }
    if (result.code === 'XMIT_ALREADY_ASSIGNED') {
      setAssignError('This XMIT is already linked to another user.');
      return;
    }
    setAssignError(result.message || 'Failed to assign XMIT');
  }

  async function handleDirectoryRemove(confirmation) {
    if (!removeTarget) return;
    setRemoveLoading(true);
    const result = await apiHandler(() =>
      removeDirectoryXmitApi(removeTarget.xmitNumber, confirmation)
    );
    setRemoveLoading(false);
    if (result.success) {
      setRemoveTarget(null);
      showToast('XMIT removed successfully', 'success');
      fetchDirectory();
      return;
    }
    showToast(result.message || 'Failed to remove XMIT', 'error');
  }

  return (
    <>
      <ToastBanner toast={toast} />
      <PageHeader
        title="XMIT Directory"
        subtitle="Quick Response transmitters from Stages, joined with MGL user assignments"
      />

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <input
              type="search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search XMIT, site, user…"
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm min-w-[220px]"
            />
            <select
              value={assigned}
              onChange={(e) => { setAssigned(e.target.value); setPage(1); }}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All assignments</option>
              <option value="true">Assigned only</option>
              <option value="false">Unassigned only</option>
            </select>
          </div>
          <button
            type="button"
            onClick={handleProvision}
            disabled={provisionLoading}
            className="px-4 py-2 bg-sky-600 text-white rounded-lg text-sm font-semibold hover:bg-sky-700 disabled:opacity-60"
          >
            {provisionLoading ? 'Provisioning…' : 'Provision new XMIT'}
          </button>
        </div>
        {(stagesSource || stagesError || directoryHint) && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#64748B', display: 'grid', gap: 6 }}>
            {stagesSource && (
              <p style={{ margin: 0 }}>
                Data source: <strong>{stagesSource}</strong>
              </p>
            )}
            {stagesError && (
              <p style={{ margin: 0, color: '#B45309' }}>
                Stages list: {stagesError}
              </p>
            )}
            {directoryHint && (
              <p style={{ margin: 0, color: '#475569' }}>{directoryHint}</p>
            )}
          </div>
        )}
      </div>

      <div className="card">
        {loading ? (
          <p style={{ color: '#64748B', fontSize: 13 }}>Loading XMIT directory…</p>
        ) : rows.length === 0 ? (
          <p style={{ color: '#64748B', fontSize: 13 }}>No XMIT records found.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>XMIT</th>
                  <th style={{ textAlign: 'left' }}>Site / Dev</th>
                  <th style={{ textAlign: 'left' }}>Service</th>
                  <th style={{ textAlign: 'left' }}>Assigned to</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.xmitNumber}>
                    <td style={{ fontFamily: "'DM Mono',monospace", fontSize: 12 }}>{row.xmitNumber}</td>
                    <td style={{ fontSize: 12, color: '#475569' }}>
                      {row.siteNum || '—'} / {row.devNum || '—'}
                    </td>
                    <td><InServiceBadge inService={row.inService} /></td>
                    <td>
                      {row.assignedUser ? (
                        <Link to={`/users/${row.assignedUserId}`} style={{ fontSize: 13, color: '#074f78', fontWeight: 600 }}>
                          {row.assignedUser.name}
                        </Link>
                      ) : (
                        <span style={{ fontSize: 12, color: '#94A3B8' }}>Unassigned</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: 8 }}>
                        {!row.assignedUserId && (
                          <button
                            type="button"
                            onClick={() => { setAssignError(''); setAssignTarget(row); }}
                            className="px-2.5 py-1 text-xs font-semibold rounded-lg border border-sky-200 text-sky-700 bg-sky-50"
                          >
                            Assign
                          </button>
                        )}
                        {row.assignedUserId && (
                          <button
                            type="button"
                            onClick={() => setRemoveTarget({
                              xmitNumber: row.xmitNumber,
                              user: row.assignedUser,
                            })}
                            className="px-2.5 py-1 text-xs font-semibold rounded-lg border border-amber-200 text-amber-700 bg-amber-50"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={20}
            onPageChange={setPage}
            loading={loading}
          />
        </div>
      </div>

      <DirectoryAssignModal
        row={assignTarget}
        open={Boolean(assignTarget)}
        loading={assignLoading}
        error={assignError}
        onCancel={() => { if (!assignLoading) setAssignTarget(null); }}
        onConfirm={handleDirectoryAssign}
      />

      <RemoveXmitModal
        user={removeTarget?.user}
        xmitNumber={removeTarget?.xmitNumber}
        open={Boolean(removeTarget)}
        loading={removeLoading}
        onCancel={() => { if (!removeLoading) setRemoveTarget(null); }}
        onConfirm={handleDirectoryRemove}
      />
    </>
  );
}
